import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Droplets, 
  IndianRupee, 
  Wallet,
  AlertCircle
} from 'lucide-react';
import { 
  AreaChart, Area, 
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { dashboardData } from '../mockData';

const Dashboard = () => {
  const [liveTanks, setLiveTanks] = useState([]);
  const [totalLiters, setTotalLiters] = useState(0);
  const [expectedSalesAmount, setExpectedSalesAmount] = useState(0);
  const [productSales, setProductSales] = useState({
    Petrol: { volume: 0, amount: 0 },
    Diesel: { volume: 0, amount: 0 },
    Xtragreen: { volume: 0, amount: 0 }
  });
  const [monthlyVolumeTrend, setMonthlyVolumeTrend] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    fetch('/api/dashboard', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setLiveTanks(data.tanks || []);
          setTotalLiters(data.totalSalesLiters || 0);
          setExpectedSalesAmount(data.expectedSalesAmount || 0);
          setProductSales(data.productSales || {
            Petrol: { volume: 0, amount: 0 },
            Diesel: { volume: 0, amount: 0 },
            Xtragreen: { volume: 0, amount: 0 }
          });
          
          if (data.monthlyVolumeTrend) {
            const formattedTrend = data.monthlyVolumeTrend.map(d => {
              const [year, month, day] = d.date.substring(0, 10).split('-').map(Number);
              const dateObj = new Date(year, month - 1, day);
              return {
                ...d,
                dateStr: dateObj.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
              };
            });
            setMonthlyVolumeTrend(formattedTrend);
          }
        }
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);
  return (
    <div className="space-y-6 pb-12">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-slate-500 text-sm mt-1">Live snapshot of today's operations and inventory.</p>
        </div>
        <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 shadow-sm">
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Tank Inventory Section */}
      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-2">Underground Tanks Inventory</h2>
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading live inventory from database...</p>
      ) : liveTanks.length === 0 ? (
        <p className="text-sm text-amber-600 bg-amber-50 p-4 rounded-xl border border-amber-200">
          No tanks found. Please set them up in Master Configuration and save your Start of Day readings.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {liveTanks.map(tank => {
          const fillPercentage = (tank.currentStock / tank.capacity) * 100;
          const isLow = fillPercentage < 25;
          const barColor = tank.type === 'Petrol' ? 'bg-orange-500' : tank.type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500';
          const bgColor = tank.type === 'Petrol' ? 'bg-orange-50' : tank.type === 'Diesel' ? 'bg-blue-50' : 'bg-emerald-50';
          
          return (
            <div key={tank.id} className="glass-panel p-5 relative overflow-hidden flex flex-col justify-between h-40 group hover:border-slate-300 transition-colors">
              <div className="flex justify-between items-start z-10">
                <div>
                  <h3 className="font-bold text-slate-800">{tank.name}</h3>
                  <p className="text-xs font-semibold text-slate-500">{tank.type}</p>
                </div>
                {isLow && (
                  <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100">
                    <AlertCircle className="w-3 h-3" /> LOW
                  </span>
                )}
              </div>

              <div className="z-10 mt-4">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-2xl font-bold text-slate-900">{tank.currentStock.toLocaleString()} L</span>
                  <span className="text-sm font-medium text-slate-500">/ {tank.capacity.toLocaleString()} L</span>
                </div>
                {/* Progress Bar representation */}
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200 shadow-inner">
                  <div 
                    className={`h-full ${barColor} transition-all duration-1000 ease-in-out ${isLow ? 'animate-pulse' : ''}`}
                    style={{ width: `${fillPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Background decorative element */}
              <div className={`absolute -right-10 -bottom-10 w-32 h-32 rounded-full blur-3xl opacity-40 ${bgColor} pointer-events-none group-hover:opacity-60 transition-opacity`}></div>
            </div>
          );
        })}
      </div>
      )}

      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-2">Previous Day Sales Breakdown</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Summary Card */}
        <div className="glass-panel p-6 bg-primary-600 text-white relative overflow-hidden flex flex-col justify-between md:col-span-3 shadow-lg shadow-primary-500/20">
          <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
          <div className="absolute left-0 bottom-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -ml-20 -mb-20 pointer-events-none"></div>
          
          <h3 className="font-bold text-white/90 mb-6 flex items-center gap-2 relative z-10 text-lg">
            <TrendingUp className="w-5 h-5"/> Total Combined Sales (Previous Day)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 relative z-10">
            <div>
              <p className="text-primary-100 text-sm font-semibold mb-1">Total Volume</p>
              <p className="text-4xl font-extrabold tracking-tight">{totalLiters.toLocaleString()} L</p>
            </div>
            <div>
              <p className="text-primary-100 text-sm font-semibold mb-1">Total Expected Amount</p>
              <p className="text-4xl font-extrabold tracking-tight">₹{expectedSalesAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {['Petrol', 'Diesel', 'Xtragreen'].map(type => {
          const stats = productSales[type] || { volume: 0, amount: 0 };
          const colorClass = type === 'Petrol' ? 'text-orange-600 bg-orange-50' : type === 'Diesel' ? 'text-blue-600 bg-blue-50' : 'text-emerald-600 bg-emerald-50';
          return (
            <div key={type} className="glass-panel p-5 flex flex-col justify-between hover:border-slate-300 transition-colors">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${colorClass}`}>
                    <Droplets className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg">{type}</h3>
                </div>
              </div>
              <div className="space-y-4 mt-2">
                <div className="flex justify-between items-end border-b border-slate-100 pb-3">
                  <span className="text-sm font-semibold text-slate-500">Volume Sold</span>
                  <span className="text-xl font-bold text-slate-900">{stats.volume.toLocaleString()} L</span>
                </div>
                <div className="flex justify-between items-end">
                  <span className="text-sm font-semibold text-slate-500">Expected Amount</span>
                  <span className="text-xl font-bold text-slate-900">₹{stats.amount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 mt-6">
        <div className="glass-panel p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Current Month Volume Trend</h2>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyVolumeTrend}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="dateStr" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} minTickGap={20} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dx={-10} tickFormatter={(value) => `${(value/1000).toFixed(1)}k L`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(value) => [`${value.toLocaleString(undefined, {maximumFractionDigits: 1})} L`, 'Fuel Sold']}
                  labelStyle={{ fontWeight: 'bold', color: '#334155' }}
                />
                <Area type="monotone" dataKey="volume" stroke="#0ea5e9" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
